import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
import config from '../config';

function AgentRuleBasedCall() {
  const { agentCustomId } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [ruleDefinitions, setRuleDefinitions] = useState([]);
  const [newCustomRules, setNewCustomRules] = useState({});
  const [formData, setFormData] = useState({}); // { ruleName: [attributes] }

  useEffect(() => {
    if (agentCustomId) {
      fetchRuleBasedCallData();
      loadRuleDefinitions();
    }
  }, [agentCustomId]);

  const loadRuleDefinitions = async () => {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/api/rule-definitions`, {
        params: { isActive: 'true' }
      });
      setRuleDefinitions(response.data || []);
      
      // Initialize newCustomRules for each rule
      const initialCustomRules = {};
      response.data.forEach(rule => {
        initialCustomRules[rule.name] = '';
      });
      setNewCustomRules(initialCustomRules);
    } catch (error) {
      console.error('Error loading rule definitions:', error);
    }
  };

  const fetchRuleBasedCallData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${config.apiBaseUrl}/api/agent-rule-based-calls/${agentCustomId}`);
      setFormData(response.data.rules || {});
    } catch (error) {
      console.error('Error fetching rule-based call data:', error);
      setError('Failed to load rule-based call configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRule = (ruleName) => {
    setFormData(prev => ({
      ...prev,
      [ruleName]: prev[ruleName] ? [...prev[ruleName], ''] : ['']
    }));
  };

  const handleRuleChange = (ruleName, index, value) => {
    setFormData(prev => ({
      ...prev,
      [ruleName]: prev[ruleName] ? prev[ruleName].map((item, i) => i === index ? value : item) : []
    }));
  };

  const handleRemoveRule = (ruleName, index) => {
    setFormData(prev => ({
      ...prev,
      [ruleName]: prev[ruleName] ? prev[ruleName].filter((_, i) => i !== index) : []
    }));
  };

  const handleAddFromSelect = (ruleName, selectedValue) => {
    if (selectedValue) {
      setFormData(prev => ({
        ...prev,
        [ruleName]: prev[ruleName] ? [...prev[ruleName], selectedValue] : [selectedValue]
      }));
    }
  };

  const handleAddCustomRule = (ruleName) => {
    const customValue = newCustomRules[ruleName] || '';
    if (customValue && customValue.trim()) {
      setFormData(prev => ({
        ...prev,
        [ruleName]: prev[ruleName] ? [...prev[ruleName], customValue.trim()] : [customValue.trim()]
      }));
      setNewCustomRules(prev => ({
        ...prev,
        [ruleName]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Filter out empty strings
      const cleanedData = {};
      Object.keys(formData).forEach(ruleName => {
        const filtered = formData[ruleName].filter(item => item && item.trim().length > 0);
        if (filtered.length > 0) {
          cleanedData[ruleName] = filtered;
        }
      });

      await axios.post(`${config.apiBaseUrl}/api/agent-rule-based-calls/${agentCustomId}`, {
        rules: cleanedData
      });
      setSuccess('Rule-based call configuration saved successfully!');
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Error saving rule-based call data:', error);
      setError('Failed to save rule-based call configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    window.close();
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 2, mb: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            Rule Based Definition
          </Typography>
          <Button
            variant="outlined"
            onClick={handleClose}
            size="small"
          >
            Close
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {ruleDefinitions.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No rules defined yet. Please create rules in Settings first.
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {ruleDefinitions.map((ruleDef) => {
              const ruleName = ruleDef.name;
              const fieldRules = formData[ruleName] || [];

              return (
                <Grid item xs={12} key={ruleDef._id}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                    {ruleName}
                  </Typography>

                  {/* Existing Rules */}
                  {fieldRules.map((rule, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={rule}
                        onChange={(e) => handleRuleChange(ruleName, index, e.target.value)}
                        placeholder="Enter attribute text"
                      />
                      <IconButton
                        onClick={() => handleRemoveRule(ruleName, index)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}

                  {/* Add New Rule Section */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', mt: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter attribute text"
                      value={newCustomRules[ruleName] || ''}
                      onChange={(e) => setNewCustomRules(prev => ({
                        ...prev,
                        [ruleName]: e.target.value
                      }))}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddCustomRule(ruleName);
                        }
                      }}
                    />
                    <IconButton
                      onClick={() => handleAddCustomRule(ruleName)}
                      color="primary"
                      size="small"
                      title="Add Attribute"
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>

                  {/* Display Selected Rules as Chips */}
                  {fieldRules.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {fieldRules.map((rule, index) => (
                        <Chip
                          key={index}
                          label={rule}
                          onDelete={() => handleRemoveRule(ruleName, index)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}
                </Grid>
              );
            })}

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={24} /> : 'Submit'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
}

export default AgentRuleBasedCall;
